import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import { FavoriteBuildingsResponse } from 'apolloClient/types/FavoriteBuilding';
import { FavoriteListingsResponse } from 'apolloClient/types/FavoriteListing';
import { useAuth } from 'components/Auth/AuthProvider';
import { PropertyType } from 'components/UnitsFilters/types';
import Favorite from 'components/icons/Favorite';
import { useFavoriteBuilding } from 'components/SavedHouses/SavedFavoritesBuildingsContext';
import { useFavorite } from 'components/SavedHouses/SavedFavoritesContext';

type GalleryFavoriteButtonProps = {
  isSaved: boolean;
  isLogged: boolean;
  onClick: (event: React.MouseEvent) => void;
  showLabel: boolean;
  useLargeIcons: boolean;
};

const GalleryFavoriteButton: React.FC<GalleryFavoriteButtonProps> = ({
  isSaved,
  isLogged,
  onClick,
  showLabel,
  useLargeIcons,
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      aria-label={isSaved ? 'Delete from Favorites' : 'Save to Favorites'}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="cursor-pointer group flex items-center uppercase h-7 gap-[4px] !w-auto"
    >
      <Favorite
        fill={isSaved && isLogged}
        hover={hovered}
        width={useLargeIcons ? 25 : undefined}
        height={useLargeIcons ? 23 : undefined}
        desktopWidth={useLargeIcons ? undefined : 18}
        desktopHeight={useLargeIcons ? undefined : 14}
        darkGold
      />
      <span
        className={classNames(
          'pt-px hover-animation text-14-18-0.3 font-sohneBuch',
          showLabel ? 'md:block' : '!hidden',
          { 'text-gold-hover': hovered }
        )}
      >
        {isSaved ? 'Saved' : 'Save'}
      </span>
    </button>
  );
};

const BUILDING_FAVORITES_STORAGE_KEY = 'saveFavoritesBuildingsTmp';
const LISTING_FAVORITES_STORAGE_KEY = 'saveFavoritesTmp';

export const BuildingGalleryFavoriteButtonV2: React.FC<{
  buildingId: number;
  userFavorites?: FavoriteBuildingsResponse;
  showLabel: boolean;
  useLargeIcons: boolean;
}> = ({ buildingId, userFavorites, showLabel, useLargeIcons }) => {
  const [onSaveAction, setOnSaveAction] = useState(false);
  const { isLogged, openHeaderPopup } = useAuth();
  const { createFavorite, deleteFavorite, getFavorites } =
    useFavoriteBuilding();

  const favorite = userFavorites?.data?.find(
    ({ attributes }) => attributes?.building?.id === buildingId
  );
  const isSaved = !!favorite?.id;

  useEffect(() => {
    if (isLogged && onSaveAction) {
      setOnSaveAction(false);
      const favoriteData = localStorage.getItem(BUILDING_FAVORITES_STORAGE_KEY);
      if (favoriteData) {
        const restoreFavorite = async () => {
          try {
            const created = await createFavorite(JSON.parse(favoriteData));
            if (created) {
              localStorage.removeItem(BUILDING_FAVORITES_STORAGE_KEY);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Data is not a JSON string:', favoriteData);
            // eslint-disable-next-line no-console
            console.error('Error parsing JSON createFavorite', error);
          }
        };
        restoreFavorite();
      }
    }
  }, [createFavorite, isLogged, onSaveAction]);

  const clickHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLogged) {
      localStorage.setItem(
        BUILDING_FAVORITES_STORAGE_KEY,
        JSON.stringify({ building: buildingId, alertFrequency: 'Daily' })
      );
      return openHeaderPopup({
        successHandler: () => {
          getFavorites();
          setOnSaveAction(true);
        },
      });
    }

    isSaved
      ? deleteFavorite({ id: favorite.id, buildingId })
      : createFavorite({ building: buildingId, alertFrequency: 'Daily' });
  };

  return (
    <GalleryFavoriteButton
      isSaved={isSaved}
      isLogged={isLogged}
      onClick={clickHandler}
      showLabel={showLabel}
      useLargeIcons={useLargeIcons}
    />
  );
};

export const UnitGalleryFavoriteButtonV2: React.FC<{
  unitId: number;
  type: PropertyType;
  userFavorites?: FavoriteListingsResponse;
  showLabel: boolean;
  useLargeIcons: boolean;
}> = ({ unitId, type, userFavorites, showLabel, useLargeIcons }) => {
  const [onSaveAction, setOnSaveAction] = useState(false);
  const { isLogged, openHeaderPopup } = useAuth();
  const { createFavorite, deleteFavorite, getFavorites } = useFavorite();

  const favorite = userFavorites?.data?.find(
    ({ attributes }) =>
      attributes?.type === type && attributes?.unit?.data?.id === unitId
  );
  const isSaved = !!favorite?.id;

  useEffect(() => {
    if (isLogged && onSaveAction) {
      setOnSaveAction(false);
      const favoriteData = localStorage.getItem(LISTING_FAVORITES_STORAGE_KEY);
      if (favoriteData) {
        const restoreFavorite = async () => {
          try {
            const created = await createFavorite(JSON.parse(favoriteData));
            if (created) {
              localStorage.removeItem(LISTING_FAVORITES_STORAGE_KEY);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Data is not a JSON string:', favoriteData);
            // eslint-disable-next-line no-console
            console.error('Error parsing JSON createFavorite', error);
          }
        };
        restoreFavorite();
      }
    }
  }, [createFavorite, isLogged, onSaveAction]);

  const clickHandler = () => {
    if (!isLogged) {
      localStorage.setItem(
        LISTING_FAVORITES_STORAGE_KEY,
        JSON.stringify({ type, unit: unitId, alertFrequency: 'Daily' })
      );
      return openHeaderPopup({
        successHandler: () => {
          getFavorites();
          setOnSaveAction(true);
        },
      });
    }

    isSaved
      ? deleteFavorite({ id: favorite.id, type, unitId })
      : createFavorite({ type, unit: unitId, alertFrequency: 'Daily' });
  };

  return (
    <GalleryFavoriteButton
      isSaved={isSaved}
      isLogged={isLogged}
      onClick={clickHandler}
      showLabel={showLabel}
      useLargeIcons={useLargeIcons}
    />
  );
};
